/**
 * Facturación: el estado real de la factura de cada cobro, y la identidad
 * fiscal propia — la otra mitad del hub de «Mi consultorio» (dueño,
 * 2-sep-2026): «la parte de los pagos».
 *
 * El pedido automático de factura YA EXISTE (`domain/payments.ts`, al
 * confirmar un pago): se dispara solo cuando el `billing_profile` del
 * pagador tiene cédula cargada. Lo que faltaba era que la propia persona
 * pudiera VER en qué quedó cada factura y CARGAR su cédula cuando falta —
 * hoy sin eso, Hacienda no deja emitir y solo el admin lo podía arreglar.
 *
 * Nunca se inventa un dato fiscal acá: los campos nacen vacíos hasta que la
 * persona los llena, y el formulario es el único camino para completarlos.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  cedulaLabel, INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE, periodLabel, TAX_ID_TYPE,
  type InvoiceStatus, type PaymentStatus, type TaxIdType,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Empty, Field, KeyValueRow, Modal, Note,
} from '@/components/ui';

/** No hay mapa de etiquetas en `@cleanblue/shared` para este enum todavía
 *  (misma nota que en `admin/DoctorProfile.tsx`, que tiene el mismo mapa). */
const TAX_ID_TYPE_LABEL: Record<TaxIdType, string> = {
  FISICA: 'Cédula física',
  JURIDICA: 'Cédula jurídica',
  DIMEX: 'DIMEX',
  NITE: 'NITE',
};

interface BillingProfile {
  id: string; legal_name: string; tax_id: string | null; tax_id_type: TaxIdType | null;
  billing_email: string | null; phone: string | null; address: string | null;
}

interface BillingProfileResponse { party_id: string | null; profile: BillingProfile | null }

interface ChargeRow {
  id: string; period_start: string; invoice_status: InvoiceStatus; payment_status: PaymentStatus;
  unit_code: string; service_name_snapshot: string;
}

export function Facturacion({ defaultLegalName }: { defaultLegalName: string }) {
  const billing = useApi<BillingProfileResponse>('/portal/billing-profile');
  const charges = useApi<{ charges: ChargeRow[] }>('/portal/charges');
  const [editando, setEditando] = useState(false);

  const profile = billing.data?.profile ?? null;
  const faltaCedula = billing.data ? !profile?.tax_id : false;

  return (
    <section className="msec">
      <div className="msec__title">Facturación</div>

      <Async query={billing}>
        {(data) => (
          <>
            {data.party_id == null ? (
              <Note tone="info">
                Todavía no tenés una identidad de facturación asociada. Escribile a Clean Blue para que te la asignen.
              </Note>
            ) : faltaCedula ? (
              // El aviso va ANTES del formulario: sin cédula, Hacienda no
              // permite emitir factura electrónica — es la causa real de por
              // qué nunca llega, no un fallo del sistema.
              <Note tone="warn">
                Falta tu cédula fiscal. Sin ella no se puede emitir tu factura electrónica cuando pagás.
              </Note>
            ) : (
              <div className="mcard mcard--par">
                <KeyValueRow label="Razón social" style={{ paddingTop: 0 }}>{profile!.legal_name}</KeyValueRow>
                <KeyValueRow label={TAX_ID_TYPE_LABEL[profile!.tax_id_type!] ?? 'Cédula'}>
                  {cedulaLabel(profile!.tax_id, profile!.tax_id_type)}
                </KeyValueRow>
                <KeyValueRow label="Correo de factura" style={{ borderBottom: 0 }}>
                  {profile!.billing_email ?? '—'}
                </KeyValueRow>
              </div>
            )}

            {data.party_id != null && (
              <Button onClick={() => setEditando(true)} style={{ marginTop: 8 }}>
                {faltaCedula ? 'Completar datos fiscales' : 'Corregir datos fiscales'}
              </Button>
            )}
          </>
        )}
      </Async>

      <div className="msec__title mt-2">Tus últimas facturas</div>
      <Async query={charges} empty={(d) => d.charges.length === 0 && (
        <Empty icon="documento" title="Todavía no tenés cobros" />
      )}>
        {(data) => (
          <div className="mcard mcard--par">
            {data.charges.slice(0, 5).map((c, i, arr) => (
              <KeyValueRow
                key={c.id}
                label={(
                  <>
                    {periodLabel(c.period_start)}
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
                      {c.unit_code} · {c.service_name_snapshot}
                    </span>
                  </>
                )}
                style={i === arr.length - 1 ? { borderBottom: 0 } : undefined}
              >
                <Badge tone={INVOICE_STATUS_TONE[c.invoice_status]}>
                  {INVOICE_STATUS_LABEL[c.invoice_status]}
                </Badge>
                {c.invoice_status === 'ISSUED' && (
                  <Link className="small" style={{ marginLeft: 8 }} to={`/portal/cobros/${c.id}`}>
                    Ver
                  </Link>
                )}
              </KeyValueRow>
            ))}
          </div>
        )}
      </Async>

      {editando && (
        <BillingProfileModal
          profile={profile}
          defaultLegalName={defaultLegalName}
          onClose={() => setEditando(false)}
        />
      )}
    </section>
  );
}

/**
 * El alta o la corrección de la identidad fiscal propia. Es un `PUT`
 * idempotente: si ya existe un perfil lo corrige, si no existe lo crea — el
 * Worker decide cuál de los dos según lo que encuentre (`portal.ts`).
 */
function BillingProfileModal({ profile, defaultLegalName, onClose }: {
  profile: BillingProfile | null; defaultLegalName: string; onClose: () => void;
}) {
  const [taxIdType, setTaxIdType] = useState<TaxIdType>(profile?.tax_id_type ?? 'FISICA');
  const [taxId, setTaxId] = useState(profile?.tax_id ?? '');
  const [legalName, setLegalName] = useState(profile?.legal_name || defaultLegalName);
  const [billingEmail, setBillingEmail] = useState(profile?.billing_email ?? '');
  const [phone, setPhone] = useState(profile?.phone ?? '');
  const [address, setAddress] = useState(profile?.address ?? '');

  const save = useApiMutation(
    () => api.put('/portal/billing-profile', {
      legal_name: legalName.trim(),
      // Siempre mandados, aunque queden vacíos: una clave AUSENTE del todo
      // rompe la validación del Worker con algunos campos opcionales de Zod
      // 4 (ver el comentario en `portal.ts`, `billingProfileSelfInput`).
      tax_id: taxId.trim(),
      tax_id_type: taxId.trim() ? taxIdType : null,
      billing_email: billingEmail.trim(),
      phone: phone.trim(),
      address: address.trim(),
    }),
    ['/portal/billing-profile'],
    { onSuccess: onClose },
  );

  const valid = legalName.trim().length > 0;

  return (
    <Modal
      title={profile ? 'Corregir datos fiscales' : 'Completar datos fiscales'}
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      )}
    >
      {save.error && <Note tone="danger">{save.error.message}</Note>}

      <Field label="Nombre o razón social" required>
        <input className="input" value={legalName} onChange={(e) => setLegalName(e.target.value)} autoFocus />
      </Field>

      <Field label="Tipo de identificación">
        <select className="select" value={taxIdType} onChange={(e) => setTaxIdType(e.target.value as TaxIdType)}>
          {TAX_ID_TYPE.map((t) => <option key={t} value={t}>{TAX_ID_TYPE_LABEL[t]}</option>)}
        </select>
      </Field>

      <Field label="Número de cédula" hint="Sin ella, Hacienda no permite emitir tu factura.">
        <input className="input" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
      </Field>

      <Field label="Correo para factura electrónica">
        <input
          className="input" type="email" value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
        />
      </Field>

      <Field label="Teléfono">
        <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>

      <Field label="Dirección">
        <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} />
      </Field>
    </Modal>
  );
}
